import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faEye, faBullseye, faUsers, faBuilding, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const VisionMission: React.FC = () => {
  const missionPoints = [
    {
      icon: faUsers,
      text: "Equip people with the skills they need to succeed professionally."
    },
    {
      icon: faBuilding,
      text: "Assist companies with specialized staffing and training solutions."
    },
    {
      icon: faHandshake,
      text: "Close the talent-industry gap with knowledgeable consulting."
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="section-title"
            >
              Vision & Mission
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-lg md:text-xl text-gray-700 mt-8"
            >
              Our guiding principles that drive everything we do at Skill Point Consultants.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon={faEye} className="text-primary text-xl" />
                </div>
                <h2 className="text-3xl font-bold">Our Vision</h2>
              </div>
              
              <div className="pl-16">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  className="p-8 bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-custom border-l-4 border-primary"
                >
                  <p className="text-xl text-gray-800 italic font-serif">
                    "To build a workforce prepared for the future by becoming the most reliable partner for business excellence, career advancement, and skill development."
                  </p>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative"
            >
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-primary/5 rounded-full filter blur-3xl"></div>
              <div className="aspect-square flex items-center justify-center p-8">
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    rotate: [0, 2, 0, -2, 0],
                  }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="relative"
                >
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary to-secondary opacity-20 blur-xl"></div>
                  <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full bg-white shadow-custom flex items-center justify-center overflow-hidden">
                    <div className="text-center p-8">
                      <FontAwesomeIcon icon={faLightbulb} className="text-4xl text-primary mb-4" />
                      <h3 className="text-xl font-bold text-secondary mb-2">Future-Ready</h3>
                      <p className="text-gray-600">Building excellence through talent development</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mr-4">
                <FontAwesomeIcon icon={faBullseye} className="text-secondary text-xl" />
              </div>
              <motion.h2
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="text-3xl font-bold"
              >
                Our Mission
              </motion.h2>
            </div>
            
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="max-w-3xl mx-auto text-lg text-gray-700"
            >
              At Skill Point Consultants, we're committed to these core objectives that drive our daily operations:
            </motion.p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12"
          >
            {missionPoints.map((point, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="card hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="h-2 bg-accent"></div>
                <div className="p-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-secondary/10 flex items-center justify-center mb-6">
                    <FontAwesomeIcon icon={point.icon} className="text-secondary text-2xl" />
                  </div>
                  <p className="text-center text-gray-700 text-lg">
                    🔹 {point.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-8 text-white"
          >
            Our Core Values
          </motion.h2>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <motion.div variants={fadeInUp} className="p-6 bg-white/10 backdrop-blur-sm rounded-lg">
              <h3 className="text-xl font-bold mb-2">Excellence</h3>
              <p className="text-white/90">We strive for excellence in everything we do.</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="p-6 bg-white/10 backdrop-blur-sm rounded-lg">
              <h3 className="text-xl font-bold mb-2">Integrity</h3>
              <p className="text-white/90">We operate with honesty and transparency.</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="p-6 bg-white/10 backdrop-blur-sm rounded-lg">
              <h3 className="text-xl font-bold mb-2">Innovation</h3>
              <p className="text-white/90">We embrace change and foster creative solutions.</p>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="p-6 bg-white/10 backdrop-blur-sm rounded-lg">
              <h3 className="text-xl font-bold mb-2">Collaboration</h3>
              <p className="text-white/90">We build strong partnerships with clients and candidates.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default VisionMission; 